import { Button, Collection, Flex, Loader } from "@aws-amplify/ui-react"
import type { FeedComponentDto } from "@strtmn/sds/types"
import { I18n } from "aws-amplify/utils"
import { Suspense, useContext, useState } from "react"
import { FaMountain, FaPenToSquare, FaXmark } from "react-icons/fa6"
import { Await, Link, useLoaderData, useParams } from "react-router-dom"
import { UserDataContext, UserDataContextHelper } from "../../../contexts"
import {
  getLinkToOrgFeedComponent,
  getLinkToOrgFeedComponents,
} from "../../../LinkUtils"
import {
  CURRENCY_SYMBOL,
  pricePerKgToPerLargeMass,
  UNIT_OF_LARGE_MASS,
} from "../../../org-settings"
import {
  ensureImagePath,
  getFeedComponentGroupLabel,
} from "../-components/FeedComponentUtils"
import GenauigkeitPerFeedComponent from "../-components/GenauigkeitPerFeedComponent"
import RecipeItem from "../-components/RecipeItem"
import SecondaryListItem from "../-components/SecondaryListItem"
import VerbrauchPerFeedComponent from "../-components/VerbrauchPerFeedComponent"

import "../RecipeManagement.css"

export default function FeedComponent() {
  const params = useParams()
  const [bgImageSrc, setBgImageSrc] = useState<string>("")
  const { feedingStats } = useLoaderData() as { feedingStats: Promise<any> }

  const { userData } = useContext(UserDataContext)
  const org = UserDataContextHelper.findOrg(params["orgId"]!, userData!)
  const feedComponent: FeedComponentDto =
    UserDataContextHelper.findFeedComponent(org, params["feedComponentId"]!)

  const recipesFiltered = UserDataContextHelper.findRecipes(org).filter((r) => {
    let recipeContainsFc = false
    r.RecipeComponents.forEach((rfc) => {
      if (rfc.feed_component_id === feedComponent.id) {
        recipeContainsFc = true
        return
      }
    })
    return recipeContainsFc
  })

  ensureImagePath(feedComponent, bgImageSrc, setBgImageSrc)

  return (
    <>
      <div className="rm-view-page">
        <div
          className="hdr-img"
          style={{ backgroundImage: `url('${bgImageSrc}')` }}
        />
        <Link to="..">
          <Button className="button-close has-icon">
            <span role="img" aria-label="Schließen">
              <FaXmark />
            </span>
          </Button>
        </Link>
        <div className="content-overlay">
          <div className="header">
            <div className="info">
              <h2>{feedComponent.name}</h2>
              <div className="basic-info">
                {Number(feedComponent.ts_gehalt_percent) > 0 ? (
                  <div>
                    TS-Gehalt:{" "}
                    {`${feedComponent.ts_gehalt_percent!.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`}
                  </div>
                ) : (
                  <div>Gewicht nach Frischmasse</div>
                )}
                <div>
                  {I18n.get("Kategorie")}:{" "}
                  {I18n.get(getFeedComponentGroupLabel(feedComponent.group))}
                </div>
                <div>
                  Preis:{" "}
                  {feedComponent.price_per_kg
                    ? `${pricePerKgToPerLargeMass(feedComponent.price_per_kg).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${CURRENCY_SYMBOL} / ${UNIT_OF_LARGE_MASS} FM`
                    : "nicht angegeben"}
                </div>
              </div>
            </div>
            <Link to={getLinkToOrgFeedComponent(org, feedComponent.id, true)}>
              <Button className="button-edit has-icon">
                <span role="img" aria-label="Bearbeiten">
                  <FaPenToSquare />
                </span>
              </Button>
            </Link>
          </div>

          <Flex className="!flex-col gap-4 md:!flex-row md:!gap-0">
            <SecondaryListItem
              title="Meine Rezepte"
              _className="w-full md:!w-1/3"
            >
              {recipesFiltered.length > 0 ? (
                <Collection
                  className="w-full !mt-8"
                  type="list"
                  direction="column"
                  items={recipesFiltered}
                >
                  {(item) => {
                    return (
                      <RecipeItem
                        className="!w-full !border-none"
                        key={item.id}
                        org={org}
                        item={item}
                        hideFavStar={true}
                        hasViewButton={false}
                      />
                    )
                  }}
                </Collection>
              ) : (
                <div className="list-item-grid">
                  {feedComponent.name} wird in keinem Rezept verwendet.
                </div>
              )}
            </SecondaryListItem>

            <Suspense
              fallback={
                <SecondaryListItem
                  title="Verbrauch"
                  _className="!w-full md:!w-1/3"
                >
                  <div>{I18n.get("Lade Daten...")}</div>
                  <Loader
                    filledColor="#498e39"
                    variation="linear"
                    className="!max-w-[50%]"
                  />
                </SecondaryListItem>
              }
            >
              <Await resolve={feedingStats}>
                {(resolvedFeedingStats) => (
                  <VerbrauchPerFeedComponent
                    _className="!w-full md:!w-1/3"
                    feedComponent={feedComponent}
                    feedingStats={resolvedFeedingStats}
                  />
                )}
              </Await>
            </Suspense>
            <Suspense
              fallback={
                <SecondaryListItem
                  title="Genauigkeit"
                  _className="!w-full md:!w-1/3"
                >
                  <div>{I18n.get("Lade Daten...")}</div>
                  <Loader
                    filledColor="#498e39"
                    variation="linear"
                    className="!max-w-[50%]"
                  />
                </SecondaryListItem>
              }
            >
              <Await resolve={feedingStats}>
                {(resolvedFeedingStats) => (
                  <GenauigkeitPerFeedComponent
                    _className="!w-full md:!w-1/3"
                    feedComponent={feedComponent}
                    feedingStats={resolvedFeedingStats}
                  />
                )}
              </Await>
            </Suspense>
          </Flex>
        </div>
      </div>

      {/* <small>* Die Icons "xmark (light)", "pen-to-square (light)" benötigen FontAwesome Pro, kostet 49,- oder 99,- pro Jahr.</small> */}
    </>
  )
}

export const Component = FeedComponent

export const ErrorComponent = () => {
  const params = useParams()
  const { userData } = useContext(UserDataContext)
  const org = UserDataContextHelper.findOrg(params["orgId"]!, userData!)

  const icon = <FaMountain className="w-5 _h-3.5_ relativ text-[#3E9E29]" />
  const title = "Komponenten"
  return (
    <>
      <div className="rm-list-page">
        <Flex className="!h-12 !pb-2 !justify-between !items-center">
          <Flex className="!h-6 !justify-start !items-center gap-3">
            {icon}
            <div className="text-xl font-semibold">{title}</div>
          </Flex>
        </Flex>

        <div className="error mb-4 pl-2">
          Fehler: Komponente nicht gefunden.
        </div>

        <Link to={getLinkToOrgFeedComponents(org)}>
          <Button>Zurück zur Liste</Button>
        </Link>
      </div>
    </>
  )
}
